import { TIME_PERIOD_UNIT, type TimePeriodUnit } from '@openx/types/frequencyCapping';
import type { TFunction } from 'i18next';

export function convertTimeframeToHours(value: number, unit: TimePeriodUnit): number {
  switch (unit) {
    case TIME_PERIOD_UNIT.HOUR:
      return value;
    case TIME_PERIOD_UNIT.DAY:
      return value * 24;
    case TIME_PERIOD_UNIT.WEEK:
      return value * 168;
    case TIME_PERIOD_UNIT.MONTH:
      return value * 720;
  }
}

/**
 * Map of time unit forms for proper singular/plural handling
 * Supports common time units used in frequency capping
 */
const TIME_UNIT_FORMS = {
  hour: { singular: 'hour', plural: 'hours' },
  day: { singular: 'day', plural: 'days' },
  week: { singular: 'week', plural: 'weeks' },
  month: { singular: 'month', plural: 'months' },
  hours: { singular: 'hour', plural: 'hours' },
  days: { singular: 'day', plural: 'days' },
  weeks: { singular: 'week', plural: 'weeks' },
  months: { singular: 'month', plural: 'months' },
} as const satisfies Record<string, { singular: string; plural: string }>;

/**
 * Generates the appropriate title for frequency capping fields based on the number of entries
 */
export const getFrequencyCappingTitle = (totalLength: number, index: number, t: (key: string) => string) => {
  return totalLength > 1 ? `${t('Number of Impressions')} ${index + 1}` : t('Number of Impressions');
};

interface FormatFrequencyCappingValueParams {
  impressions: string;
  unit: string;
  t: TFunction;
  value?: number | string;
}

/**
 * Formats frequency capping text using template strings with translation
 * Handles singular/plural forms of time units
 * Uses i18next interpolation with placeholders: {impressions}, {value}, {unit}
 *
 * @param params - Configuration object
 * @param params.impressions - The impression limit (e.g., "10")
 * @param params.unit - The time unit (e.g., "hours", "days")
 * @param params.t - Translation function
 * @param params.value - Optional time period value (can be string or number; when 1, uses singular form)
 * @returns Formatted and translated string
 */
export const formatFrequencyCappingValue = ({ impressions, unit, t, value }: FormatFrequencyCappingValueParams) => {
  // Convert value to number for comparison (backend may return string)
  const numValue = value !== undefined ? +value : undefined;

  // Get unit forms from map, fallback to provided unit if not found
  const unitLower = unit.toLowerCase();
  const unitForms = TIME_UNIT_FORMS[unitLower];

  // Determine display unit based on value: singular when value === 1, otherwise plural
  const displayUnit = numValue === 1 && unitForms ? unitForms.singular : (unitForms?.plural ?? unit);

  // Use translation key with placeholders and pass values as interpolation options
  if (value !== undefined) {
    return t('{impressions} Impressions per {value} {unit}', {
      impressions,
      value,
      unit: displayUnit,
    });
  }

  return t('{impressions} Impressions per {unit}', {
    impressions,
    unit: displayUnit,
  });
};
